import {
  Button,
  Card,
  Modal,
  Pagination,
  Space,
  Table,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import PageTitle from '../../components/page-title';
import styles from './styles.module.scss';
import CustomButton from 'components/custom-button';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import { shortenName } from 'utils/short-name';
import moment from 'moment';
import Filter from './component/Filter';
import ModalCreateLiveShow from './component/ModalCreateLiveShow';
import lodash from 'lodash';

const LIMIT = 10;

const initFilter = { page: 1, limit: LIMIT };

// Giờ 24h kiểu Việt Nam ("02/10/2026 13:00"); moment 'LT' ra "1:00 PM" và chữ AM/PM
// hay bị đẩy xuống dòng riêng.
const formatDateTime = (value?: string) =>
  value && moment(value).isValid() ? moment(value).format('DD/MM/YYYY HH:mm') : '';

export interface LiveShowFilter {
  limit: number;
  page: number;
  startDate?: string;
  endDate?: string;
}

const LiveShowManager = () => {
  const [loading, setLoading] = useState(true);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(initFilter as LiveShowFilter);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState(false);
  const [isOpenModalEditAndCreate, setIsOpenModalEditAndCreate] = useState(false);
  const [valueUserSelected, setvalueUserSelected] = useState<any>(null);

  const onFinish = (value: Partial<LiveShowFilter>) => {
    setFilter((preState) => ({ ...preState, ...value, page: 1 }));
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  useEffect(() => {
    const fetchPks = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.liveShow, {
          ...filter,
        });

        const makeData = res?.data?.data.map((show: any) => {
          return {
            ...show,
            groupName: show?.group?.name || '',
          };
        });
        setDataResponse({ items: makeData, totalCount: res?.data?.metadata?.total });
      } finally {
        setLoading(false);
      }
    };

    fetchPks();
  }, [filter, statusUpdateData]);

  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 72,
      ellipsis: true,
      key: 'id',
    },
    {
      title: 'Nhóm liveshow',
      dataIndex: 'groupName',
      key: 'groupName',
      render: (value) => (
        <Tooltip title={value}>
          <span>{shortenName(value || '', 60) || ''}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Ảnh',
      dataIndex: 'image',
      key: 'image',
      // Ảnh 136px + 2 × 16px đệm ô: cột không có độ rộng thì co lại và ảnh đè sang cột bên.
      width: 168,
      render: (value) => (
        <img
          width={136}
          height={72}
          className={styles.thumb}
          src={`${SERVER_FILE_ENDPOINT}/${value}`}
          alt=""
        />
      ),
    },
    {
      title: 'TikTok ID',
      key: 'user',
      render: (_, record) => {
        const username =
          lodash.get(record, 'user.username', `${record.tiktokIdol ? record.tiktokIdol : ''}`) ||
          '';
        return (
          <Typography.Text className={styles.cellText} ellipsis={{ tooltip: username }}>
            {username}
          </Typography.Text>
        );
      },
    },

    {
      title: 'Bắt đầu',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 164,
      render: (value) => <span className={styles.nowrap}>{formatDateTime(value)}</span>,
    },

    {
      title: 'Kết thúc',
      dataIndex: 'endDate',
      key: 'endDate',
      width: 164,
      render: (value) => <span className={styles.nowrap}>{formatDateTime(value)}</span>,
    },
    {
      title: 'Thao tác',
      width: 104,
      key: 'action',
      render: (_, record) => (
        <Tooltip title="Sửa">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handelOpenCreateEditModal(record)}
          />
        </Tooltip>
      ),
    },
  ];
  const onChangeStatusUpdateData = () => setStatusUpdateData(!statusUpdateData);

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  const handelOpenDeleteModal = () => {
    setIsModalDeleteOpen(true);
  };

  const handleOkDeleteModal = async () => {
    setLoading(true);
    try {
      await AxiosUtils._delete(API.deleteShow, { ids: selectedRowKeys });
      onChangeStatusUpdateData();
      notification.success({
        message: 'Thành công',
        description: 'Xóa thành công!',
        duration: 4,
      });
      setSelectedRowKeys([]);
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
      setIsModalDeleteOpen(false);
    }
  };

  const handleCancelDeleteModal = () => {
    setIsModalDeleteOpen(false);
  };

  const handelOpenCreateEditModal = (value: any) => {
    setIsOpenModalEditAndCreate(true);
    setvalueUserSelected(value);
  };

  const handleCancelCreateEditModal = () => {
    setIsOpenModalEditAndCreate(false);
    setvalueUserSelected(null);
  };

  return (
    <div>
      <PageTitle
        title="Liveshow"
        extra={
          <Space wrap>
            {hasSelected && (
              <Button danger icon={<DeleteOutlined />} onClick={handelOpenDeleteModal}>
                Xóa {selectedRowKeys.length} phòng live
              </Button>
            )}
            <CustomButton
              onClick={() => handelOpenCreateEditModal(valueUserSelected)}
              bgColor="primary-green"
              text="Thêm phòng live"
              icon={<PlusOutlined />}
            />
          </Space>
        }
      />
      <div className={styles.filterContainer}>
        <Filter onSubmit={onFinish} />
      </div>
      <Card
        className="custom-table"
        styles={{ body: { padding: 0 } }}
        style={{ overflow: 'hidden' }}
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={dataResponse.items}
          // Bảng rỗng thì bỏ scroll.x: ô "Trống" rộng hơn khung 2px → cuộn ngang thừa.
          scroll={dataResponse.items?.length ? { x: 940 } : undefined}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <div className={styles.pagination}>
            <Pagination
              showSizeChanger={false}
              current={filter.page}
              onChange={handleChangePage}
              showLessItems
              total={dataResponse.totalCount}
              pageSize={LIMIT}
            />
          </div>
        )}
      </Card>

      {isOpenModalEditAndCreate && (
        <ModalCreateLiveShow
          isOpen={isOpenModalEditAndCreate}
          handleCancelCreateEditModal={handleCancelCreateEditModal}
          valueUserSelected={valueUserSelected}
          onChangeStatusUpdateData={onChangeStatusUpdateData}
        />
      )}
      <Modal
        title="Xóa phòng live"
        open={isModalDeleteOpen}
        onOk={handleOkDeleteModal}
        onCancel={handleCancelDeleteModal}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
        confirmLoading={loading}
      >
        <p>Bạn có chắc muốn xóa {selectedRowKeys.length} phòng live đã chọn?</p>
      </Modal>
    </div>
  );
};

export default LiveShowManager;
